/* Shared, browser-local calculation rules. No personal data is persisted. */
const ageBands = {
  '18-24': [18, 24], '25-30': [25, 30], '31-50': [31, 50],
  '50-60': [50, 60], '60-75': [60, 75], '75+': [75, 120]
};
const goals = [
  ['gut', 'Maintain gut health'], ['gain', 'Weight gain'], ['loss', 'Weight loss'],
  ['energy', 'Boost energy'], ['muscle', 'Build muscle'], ['maintain', 'Maintain body weight']
];
const activities = {
  sedentary: {label: 'Mostly seated / little exercise', detail: 'A typical desk-based day', factor: 1.2, protein: 0.8},
  light: {label: 'Light activity / occasional exercise', detail: 'Light exercise 1–3 days a week', factor: 1.375, protein: 1.2},
  gym: {label: 'Regular gym', detail: 'Moderate training 3–5 days a week', factor: 1.55, protein: 1.6},
  exercise: {label: 'Regular exerciser', detail: 'Moderate sport or exercise 3–5 days a week', factor: 1.55, protein: 1.4},
  yoga: {label: 'Yoga', detail: 'Gentle to moderate sessions 1–3 days a week', factor: 1.375, protein: 1.2},
  bodybuilding: {label: 'Bodybuilder', detail: 'Hard resistance training 6–7 days a week', factor: 1.725, protein: 1.8},
  physical: {label: 'Physical job / more than 10k steps a day', detail: 'A consistently active day', factor: 1.725, protein: 1.4}
};
const fitnessGoals = [
  ['gut-muscle', 'Improve gut health alongside bodybuilding'],
  ['healthy-life', 'Maintain a healthy life & work routine'],
  ['recovery', 'Boost energy / body recovery'], ['daily-protein', 'Fuel daily protein']
];
const diets = {vegetarian: 'Vegetarian', vegan: 'Vegan', flexitarian: 'Flexitarian', anything: 'I eat anything'};
const symptoms = [
  ['ibd', 'IBD'], ['ibs', 'IBS'], ['bloating', 'Bloating with new recipes / habits'],
  ['cramps', 'Frequent cramps / vomiting'], ['none', 'Nothing'], ['private', 'Prefer not to say']
];
const LB_TO_KG = 0.45359237;
const INCH_TO_CM = 2.54;
const includesOnly = (values, allowed) => Array.isArray(values) && new Set(values).size === values.length && values.every(value => allowed.includes(value));
const number = value => value === '' || value === null || value === undefined ? NaN : Number(value);

function measurements(input) {
  return {
    kg: input.weightUnit === 'lb' ? number(input.weightLb) * LB_TO_KG : number(input.weightKg),
    cm: input.heightUnit === 'imperial' ? (number(input.feet) * 12 + number(input.inches)) * INCH_TO_CM : number(input.heightCm)
  };
}

function validate(input, step) {
  const errors = [];
  const check = index => step === undefined || step === index;
  const add = (field, message, index) => errors.push({field, message, step: index});
  // Adult equations must never be applied to the under-18 answer.
  if (input.ageBand === 'under18') return errors;
  if (check(0)) {
    if (typeof input.name !== 'string' || !input.name.trim() || input.name.trim().length > 100) add('name', 'Please enter your name (up to 100 characters).', 0);
    if (!['male', 'female'].includes(input.sex)) add('sex', 'Please choose Male or Female for the equation.', 0);
    const band = ageBands[input.ageBand];
    const age = number(input.age);
    if (!band) add('ageBand', 'Please select your age group.', 0);
    if (!Number.isInteger(age) || age < 18 || age > 120) add('age', 'Please enter your exact age in whole years, from 18 to 120.', 0);
    else if (band && (age < band[0] || age > band[1])) add('age', 'Your exact age must fall within the age group you selected.', 0);
  }
  if (check(1)) {
    const {kg, cm} = measurements(input);
    if (!['metric', 'imperial'].includes(input.heightUnit)) add('height', 'Please choose a height unit.', 1);
    if (!Number.isFinite(cm) || cm < 80 || cm > 250) add('height', 'Please check your height. This calculator supports 80–250 cm (about 2 ft 8 in–8 ft 2 in).', 1);
    if (input.heightUnit === 'imperial' && (!Number.isInteger(number(input.feet)) || number(input.feet) < 2 || number(input.feet) > 8 || !Number.isInteger(number(input.inches)) || number(input.inches) < 0 || number(input.inches) > 11)) add('height', 'Please select both feet and inches.', 1);
    const rawWeight = number(input.weightUnit === 'lb' ? input.weightLb : input.weightKg);
    if (!['kg', 'lb'].includes(input.weightUnit) || !Number.isInteger(rawWeight) || rawWeight < 1 || rawWeight > (input.weightUnit === 'lb' ? 300 : 200) || !Number.isFinite(kg) || kg < 30 || kg > 200) add('weight', 'Please check your weight. The adult estimate supports 30–200 kg; the pounds list runs up to 300 lb.', 1);
  }
  if (check(2)) {
    if (!includesOnly(input.goals, goals.map(item => item[0])) || !input.goals.length) add('goals', 'Please select at least one goal.', 2);
    else if (input.goals.filter(goal => ['gain', 'loss', 'maintain'].includes(goal)).length > 1) add('goals', 'Choose one weight direction: gain, loss or maintenance.', 2);
    if (!Object.hasOwn(activities, input.activity)) add('activity', 'Please choose the activity that best describes your typical week.', 2);
    if (!includesOnly(input.fitnessGoals, fitnessGoals.map(item => item[0])) || input.fitnessGoals.length < 2) add('fitnessGoals', 'Please select at least two fitness goals.', 2);
  }
  if (check(3)) {
    if (!Object.hasOwn(diets, input.diet)) add('diet', 'Please select your eating habit.', 3);
    if (!includesOnly(input.symptoms, symptoms.map(item => item[0])) || input.symptoms.length < 1 || input.symptoms.length > 3) add('symptoms', 'Please select one to three answers, or choose Nothing / Prefer not to say.', 3);
    else if (input.symptoms.length > 1 && input.symptoms.some(value => ['none', 'private'].includes(value))) add('symptoms', 'Nothing and Prefer not to say must each be selected on their own.', 3);
  }
  return errors;
}

function calculate(input) {
  if (input.ageBand === 'under18') return {status: 'under18'};
  const errors = validate(input);
  if (errors.length) return {status: 'invalid', errors};
  const {kg, cm} = measurements(input);
  const age = Number(input.age);
  const activity = activities[input.activity];
  // Common rounded Mifflin–St Jeor equation, kcal/day.
  const resting = 10 * kg + 6.25 * cm - 5 * age + (input.sex === 'male' ? 5 : -161);
  const maintenance = resting * activity.factor;
  const bmi = kg / ((cm / 100) ** 2);
  const notes = [];
  if (resting < 800 || maintenance < 1200 || bmi < 16 || bmi > 50) {
    return {status: 'individual', message: 'These measurements fall outside the range where this simple adult calculator can provide a useful estimate. Double-check your units; a registered dietitian can help set daily targets that fit your circumstances.'};
  }
  let adjustment = 1;
  let adjustmentLabel = 'Maintenance estimate';
  const clinicalCaution = input.symptoms.some(value => ['ibd', 'cramps'].includes(value));
  const weightChange = input.goals.some(goal => ['gain', 'loss'].includes(goal));
  if (weightChange && (clinicalCaution || age >= 75 || (input.goals.includes('loss') && bmi < 18.5))) {
    notes.push('A weight-change adjustment has not been applied. Please discuss weight-change goals with your clinician or registered dietitian. The figures shown are a general maintenance estimate.');
  } else if (input.goals.includes('loss')) {
    if (maintenance * 0.9 >= Math.max(resting, 1500)) {
      adjustment = 0.9;
      adjustmentLabel = 'Illustrative weight-loss estimate (10% below maintenance)';
    } else notes.push('A calorie reduction has not been applied because it would make this simple estimate too low. A registered dietitian can advise on a suitable target.');
  } else if (input.goals.includes('gain')) {
    adjustment = 1.1;
    adjustmentLabel = 'Illustrative weight-gain estimate (10% above maintenance)';
  }
  let proteinFactor = activity.protein;
  if (input.goals.includes('muscle') || input.fitnessGoals.includes('gut-muscle')) proteinFactor = Math.max(proteinFactor, 1.8);
  if (input.goals.includes('loss') && adjustment < 1) proteinFactor = Math.max(proteinFactor, 1.6);
  const target = Math.round(maintenance * adjustment);
  const protein = Math.round(kg * proteinFactor);
  const fat = Math.round(target * 0.3 / 9);
  const carbs = Math.round((target - protein * 4 - fat * 9) / 4);
  // Decline combinations that do not fit a plausible balanced macro split.
  if (carbs < 0 || protein * 4 > target * 0.35) return {status: 'individual', message: 'Your selected weight and training goals need a more individual nutrition assessment than this calculator can provide. A registered dietitian can help balance your protein and energy needs.'};
  // Exact energy consistency with the displayed, rounded macro amounts.
  const calories = protein * 4 + carbs * 4 + fat * 9;
  if (input.symptoms.includes('ibd')) notes.push('With IBD, needs can change with disease activity and treatment. These are healthy-adult benchmarks, not an IBD nutrition plan. Check the report with your IBD team or dietitian before using it.');
  if (input.symptoms.includes('ibs') || input.symptoms.includes('bloating')) notes.push('This calculator does not assess food tolerance or diagnose symptoms. IBS and bloating may need individual food and portion guidance from a dietitian.');
  if (input.symptoms.includes('cramps')) notes.push('Frequent cramps or vomiting should be discussed with a healthcare professional. Do not use a macro report to manage these symptoms.');
  if (age >= 75) notes.push('Energy needs in later life vary widely. Treat these figures as rough adult benchmarks and confirm them with a registered dietitian.');
  return {
    status: 'ready', name: input.name.trim().split(/\s+/)[0], kg, cm, age,
    protein, carbs, fat, calories, resting: Math.round(resting), maintenance: Math.round(maintenance),
    proteinFactor, activityFactor: activity.factor, activityLabel: activity.label,
    adjustmentLabel, diet: input.diet, notes
  };
}
const engine = Object.freeze({ageBands, goals, activities, fitnessGoals, diets, symptoms, LB_TO_KG, INCH_TO_CM, measurements, validate, calculate});
export default engine;
